import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button, Link } from 'src/components/atoms';
import styled from 'styled-components';
import '../css/Faq.css';
import * as Types from '../types';
import { isRequiredFeatureEnabled } from './auth/RequiredFeatureEnabled';

const DownloadLink = styled.a`
  text-decoration: none;
`;

export const Faq = (): React.ReactElement<HTMLElement> => {
  const features = useSelector((state: Types.State) => state.features);
  const lineManagementEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.LINE_MANAGEMENT,
  );

  const uploadUserLink = '/uploadUsers';
  const templateLink = lineManagementEnabled
    ? '/assets/Users-Demo.csv'
    : '/assets/Users-Demo-users-Demo.csv';
  return (
    <div className="faq">
      <div className="faq-breadcrumb">
        <Link to={uploadUserLink}> Upload users </Link> {'>'} FAQ upload CSV
      </div>
      <div className="faq-heading"> FAQ </div>
      <div className="faq-subheading">
        Bulk Uploading Employees to Vault HR Portal
      </div>

      <div className="faq-text">
        <p>
          In order to manually upload multiple employees into the system you can
          simply use a .csv file.
        </p>
        <p>
          The .csv will only accept the following format with the exact headers
          per column:
        </p>
      </div>
      <div className="faq-image">
        <img src="/assets/faqCsvExample.png" alt="Example CSV" />
      </div>

      <div className="faq-headers">
        <div className="faq-headers-title">Headers</div>
        <div className="faq-subheaders">
          <p>
            <span className="faq-green-text"> eid - </span>
            This is your employees unique identification number
          </p>
        </div>
        <div className="faq-subheaders">
          <p>
            <span className="faq-green-text"> email - </span>
            The employee’s email address{' '}
          </p>
        </div>
        <div className="faq-subheaders">
          <p>
            <span className="faq-green-text"> name - </span>
            The employee’s full name
          </p>
        </div>
        <div className="faq-subheaders">
          <p>
            <span className="faq-green-text"> role - </span>
            The employee’s role title
          </p>
        </div>
        <div className="faq-subheaders">
          <p>
            <span className="faq-green-text"> site - </span>
            The geographical location of the employee (useful for if you have
            multiple offices)
          </p>
        </div>
        <div className="faq-subheaders">
          <p>
            <span className="faq-green-text"> department - </span>
            The company department in which the employee works
          </p>
        </div>
      </div>

      <div className="faq-text">
        <p>
          Note: We can only accept the headers as they are specified above, if
          you use a different naming convention you will need to transform them
          to match what is specified.
        </p>
        <p>
          If you don’t want to include certain headers in your upload simply
          ignore them from the file and that information will be excluded from
          the employee upload.
        </p>
      </div>

      <div className="faq-download-button">
        <DownloadLink
          href={templateLink}
          download="Users-Demo.csv"
          type="text/csv"
        >
          <Button variant="secondary" size="medium">
            Download CSV sample
          </Button>
        </DownloadLink>
      </div>

      <div className="faq-subheading">Uploading the file</div>
      <p>
        To upload the file simply click the upload a csv file button in the
        upload users tab
      </p>
      <div className="faq-image-button">
        <img src="/assets/faqUploadUsersButton.png" alt="Example CSV" />
      </div>

      <p>
        Select the file from your computer and hit <strong>Open.</strong>
      </p>

      <p>
        If the upload is successful, you will see a preview of the employees
        that were found:
      </p>

      <div className="faq-image">
        <img src="/assets/faqUploadUsersSummary.png" alt="Example CSV" />
      </div>

      <p>From here you have two options:</p>

      <p>
        Clicking <strong>&quot;Save and Invite&quot;</strong> will add the
        employees to the vault system and send invitation emails to any new
        employees added to the system
      </p>

      <p>
        Clicking <strong>&quot;Save&quot;</strong> will add employees to the
        system but <strong>will not </strong> send any email invitations, you
        can do this manually from the Manage Users table at a later date.
      </p>

      <p>
        If the upload fails check you have the headers configured correctly and
        as specified above.{' '}
      </p>

      <div className="faq-subheading">Duplicates</div>
      <p>
        The system will automatically check for duplicates in the file. Any
        duplicated employees that are found are automatically excluded from the
        upload.
      </p>
    </div>
  );
};
